import { normalizedTranslate } from '@rbilabs/components-library';
import styled, { css } from 'styled-components';

import { IconLocked } from 'components/icons/locked';
import { IconLoyaltyPoints } from 'components/icons/loyalty-points-icon';
import { MenuCardButton } from 'components/menu-card-button';
import { Picture } from 'components/picture';
import { RewardsCarousel } from 'components/rewards-carousel';
import { LoyaltyLoader } from 'pages/loyalty/loyalty-loader';
import { primitive } from 'styles/constants/primitives';
import { PointsDisplay, PointsDisplayIcon } from 'ui-system/components/points-display';
import { delayUnmountHOC } from 'utils/delay-unmount-hoc';

export const CATEGORY_TRANSITION_TIME = 350;

interface RewardContainerProps {
  isVisible?: boolean;
}

const RewardContainer = styled.div<RewardContainerProps>``;
const CATEGORY_HEADER_HEIGHT = '2.75rem';
const CATEGORY_HEADER_WIDTH = '23.4375rem;';

export const DelayedUnmountRewardContainer = delayUnmountHOC(RewardContainer);

export const RewardCategoryAccordion = styled.li<{
  open: boolean;
  categoryHeight: number;
  isCurrentSelection: boolean;
}>`
  height: ${p => `calc(${p.open ? p.categoryHeight : 0}rem + ${CATEGORY_HEADER_HEIGHT})`};
  width: ${CATEGORY_HEADER_WIDTH};
  display: flex;
  width: 100%;
  transition: height ${CATEGORY_TRANSITION_TIME / 1000}s;
  overflow: hidden;
  flex-direction: column;
  background: ${Styles.color.white};

  ${p =>
    p.isCurrentSelection &&
    css`
      position: relative;
      z-index: ${Styles.zIndex.top};
    `}
`;

export const CategoryHeader = styled.h4`
  flex-shrink: 0;
  width: 100%;
  height: ${CATEGORY_HEADER_HEIGHT};
  padding: 0;
  margin: 0;
  border-block-end: 1px solid ${Styles.color.grey.four};
`;

export const AccordionTrigger = styled.button`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  margin: 0;
  padding: 0 1rem;
  background-color: ${Styles.color.white};
  border: none;
  cursor: pointer;

  &[aria-expanded='true'] svg {
    transition: 0.35s;
    transform: rotate(180deg);
  }
`;

export const CategoryImage = styled(Picture)`
  height: 1.7rem;
  width: 1.7rem;
  margin-inline-end: 0.75rem;
`;

export const CategoryLabel = styled.span`
  flex: 1;
  text-align: start;
  font-family: ${Styles.fontFamily.brand};
  font-size: 1.25rem;
  font-weight: ${Styles.fontWeight.normal};
`;

export const LoyaltyRewardsTitle = styled.h3`
  background: ${primitive.$white};
  margin: 0;
  padding: 1rem;
  font-family: ${Styles.fontFamily.brand};
`;

export const StyledLoyaltyLoader = styled(LoyaltyLoader)`
  height: 80vh;
`;

export const EmptyStateContainer = styled.div`
  padding: 5em 3em;
`;

export const CarouselSection = styled.div`
  width: 100%;
`;

export const CarouselHeadingContainer = styled.div`
  background: rgba(0, 0, 0, 0.04);
  padding: 0.75em 0;
  margin-block-end: 0.6em;

  ${Styles.desktop} {
    margin-block-end: 0;
    padding: 0.75rem 9rem;
  }
`;

export const CarouselHeading = styled.div`
  display: flex;
  max-width: 87.5rem;
  margin: 0 auto;
  font-style: normal;
  font-weight: 400;
  font-size: 1.125rem;
  line-height: 1.125rem;
  align-items: center;

  ${Styles.desktop} {
    margin-block-end: 0;
    max-width: 71rem;
    gap: 0.75rem;
  }
`;

export const ItemLoyaltyIcon = styled(IconLoyaltyPoints)`
  width: 1.5rem;
  margin-inline-end: 0.5rem;

  path {
    fill: ${Styles.color.alert.info.background};
  }
`;

export const CarouselHeadingLabel = styled.span`
  font-weight: 600;
`;

export const PointsNeededText = styled.p`
  font-family: ${Styles.fontFamily.base};
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.25rem;
  margin-inline-start: auto;
  margin-inline-end: 0.5rem;
  margin-block-start: 0.5rem;
  margin-block-end: 0;

  ${Styles.desktop} {
    font-size: 1.25rem;
    flex: 1 0 0;
    text-align: end;
  }
`;

export const CarouselSectionRewards = styled.div`
  display: flex;
  justify-content: center;
  max-width: 106.25rem;
  margin: 2rem auto;
  list-style: none;
`;
export const CarouselItem = styled.div`
  position: relative;
  background: white;
  width: 9.375rem;
  height: 15.5rem;
  border-radius: 0.75rem;
  margin: 0;
  padding-block-start: 0;
  padding-block-end: 0.3rem;
  padding-inline: 0.3rem;
  cursor: pointer;
  outline: ${p => p.theme.cardOutline};

  ${Styles.desktop} {
    width: 10.75rem;
    max-width: 10.75rem;
    height: 16.2rem;
    margin: 1.5rem;
    padding-block-start: 0.75rem;
    padding-block-end: 1.25rem;
    padding-inline: 0.75rem;
  }
`;

export const CarouselItemInline = styled.div`
  position: relative;
  background: ${primitive.$white};
  width: 8.5rem;
  border-radius: 0.75rem;
  margin: 0;
  padding: 0;
  cursor: pointer;
  border: 2px solid #0000001a;
  height: 11.5rem;

  ${Styles.desktop} {
    width: 10.75rem;
    height: 15.625rem;
    margin-inline: 0.5rem;
    padding: 0;
  }
`;

export const CarouselContainer = styled.div`
  justify-content: center;
  padding: 0 6rem;

  &.slider {
    .slide-current .slide-visible {
      width: unset;
    }
  }

  ${Styles.desktop} {
    padding: 0;
    max-width: 74rem;
    margin: 0 auto;
  }
`;

export const CarouselContainerInline = styled.div`
  justify-content: center;
  padding-inline: 1rem;

  &.slider {
    .slide-current .slide-visible {
      width: unset;
    }
  }

  & .slide-visible {
    padding-block: 0.1rem !important;
  }

  ${Styles.desktop} {
    padding: 0;
    width: 100%;
    margin: 0 auto;
  }
`;

export const CarouselRewardLock = styled(IconLocked)`
  margin-block: 0.313rem;
  margin-inline-start: 0.313rem;
  margin-inline-end: 0.625rem;
`;

export const CardWrapper = styled.div<{ locked?: boolean }>`
  position: relative;
  text-align: center;
`;

export const CardWrapperInline = styled.div<{ locked?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.75rem;
  width: 136px;
  height: 184px;

  ${Styles.desktop} {
    padding: 0.75rem;
    padding-block-end: 1.25rem;
    width: 172px;
    height: 250px;
  }
`;

export const PictureWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 8rem;
  margin-block-start: 0.75rem;

  ${Styles.desktop} {
    margin-block-start: 1rem;
    width: 9.25rem;
    height: 7.375rem;
  }
`;

export const PictureWrapperInline = styled.div`
  position: relative;
  margin-inline-start: -4px;

  & .inline-picture {
    width: 112px;
    height: 100px;
  }

  ${Styles.desktop} {
    & .inline-picture {
      width: 148px;
      height: 118px;
    }
  }
`;

export const CarouselRewardName = styled.div`
  font-family: ${Styles.fontFamily.base};
  font-style: normal;
  font-weight: 500;
  font-size: 1rem;
  text-align: center;
  height: 2.5rem;
  line-height: 1.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-block-start: 0.75rem;

  ${Styles.desktop} {
    font-size: 1.125rem;
  }
`;

export const CarouselRewardNameInline = styled.div`
  font-family: ${Styles.fontFamily.base};
  font-style: normal;
  font-weight: 400;
  font-size: 0.875rem;
  width: calc(100%);
  white-space: nowrap;
  display: inline-block;
  text-align: center;
  text-overflow: ellipsis;
  overflow: hidden;
  line-height: 1rem;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-block-start: 0.25rem;

  ${Styles.desktop} {
    font-size: 1.125rem;
    line-height: 1.25rem;
    height: 4.25rem;
    overflow: visible;
    overflow-y: hidden;
    text-overflow: initial;
    display: block;
    width: 100%;
    text-wrap: auto;
  }
`;

export const CarouselItemPoints = styled.div<{ locked?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${p =>
    p.locked ? Styles.color.codeBlockBackground : Styles.color.contrastBackground};
  color: ${p => (p.locked ? Styles.color.contrastBackground : primitive.$white)};
  width: fit-content;
  margin-block-start: 0.5em;
  margin-block-end: 0;
  margin-inline: auto;
  padding: 0.2em 0.6em;
  border-radius: 0.25rem;
  line-height: 1.125rem;
  font-size: 0.75rem;
  font-weight: 700;
`;

export const StyledRewardsCarousel = styled(RewardsCarousel)``;

export const InlineRewardsCarouselContainer = styled.div`
  & .responsive-carousel-card-container {
    padding-inline-start: 1.125rem !important;
    padding-block-end: 0 !important;

    ${Styles.desktop} {
      padding-inline-start: 0.75rem !important;
      padding-block-end: 0 !important;
    }
  }
`;

export const LockOverlay = styled.div`
  width: calc(100% - 1rem);
  height: 8rem;
  position: absolute;
  inset-block-start: 0;
  inset-inline-start: 0;
  background: ${primitive.$black};
  border-radius: 0.5rem;
  margin: 0 0.5rem;
  opacity: 0.5;

  ${Styles.desktop} {
    height: 7.375rem;
  }
`;

export const LockOverlayInline = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  inset-inline-start: 0;
  inset-block-start: 0;
  background: ${primitive.$black};
  border-radius: 0.75rem;
  opacity: 0.5;
`;

export const RewardPictureLock = styled(IconLocked)`
  position: absolute;
  inset-block-start: 0;
  inset-inline-start: 0;
  inset-inline-end: 0;
  inset-block-end: 0;
  display: block;
  width: 2.5rem;
  height: 2.5rem;
  margin: auto;
`;

export const RewardPictureLockInline = styled(IconLocked)`
  position: absolute;
  inset-block-start: -0.5rem;
  inset-inline-start: 0;
  inset-inline-end: 0;
  inset-block-end: 0;
  display: block;
  width: 2rem;
  height: 2rem;
  margin: auto;
`;

export const StyledPointsDisplayHeading = styled(PointsDisplay)`
  font-family: ${Styles.fontFamily.body};
  font-weight: 700;
  margin-inline-start: 0.5em;

  ${Styles.desktop} {
    margin-inline-start: 0;
  }

  ${PointsDisplayIcon} {
    width: 1.5em;
    height: 1.5em;
  }
`;

export const StyledPointsDisplayBadge = styled(PointsDisplay)`
  margin-inline: auto;
  margin-block-start: 0.5em;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.3em 0.6em;
  border-radius: 0.25rem;
  gap: 0.3em;
  background: ${Styles.color.contrastBackground};
  color: ${primitive.$white};

  ${PointsDisplayIcon} {
    width: 1.125rem;
    height: 1.125rem;
  }
`;

export const StyledPointsDisplayBadgeInline = styled(PointsDisplay)`
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.3em 0.6em;
  border-radius: 0.25rem;
  gap: 0.3em;
  background: ${Styles.color.contrastBackground};
  color: ${primitive.$white};
  margin-block-start: 0.75rem;

  ${PointsDisplayIcon} {
    width: 1.125rem;
    height: 1.125rem;
  }

  ${Styles.desktop} {
    margin-block-start: 0.625rem;
  }
`;

export const StyledPointsDisplayBadgeLocked = styled(StyledPointsDisplayBadge)`
  background: ${Styles.color.codeBlockBackground};
  color: ${Styles.color.contrastBackground};

  ${PointsDisplayIcon} {
    height: 0.9rem;
  }
`;

export const StyledPointsDisplayBadgeLockedInline = styled(StyledPointsDisplayBadge)`
  background: ${Styles.color.codeBlockBackground};
  color: ${Styles.color.contrastBackground};
  margin-block-start: 1rem;

  ${PointsDisplayIcon} {
    height: 0.9rem;
  }

  ${Styles.desktop} {
    margin-block-start: 0.625rem;
  }
`;

export const CarouselViewMoreItem = styled.div`
  background: #0000000a;
  height: 184px;
  width: 136px;
  border-radius: 0.75rem;
  margin: 0;
  padding-block-start: 0;
  padding-block-end: 0.3rem;
  padding-inline: 0.3rem;
  cursor: pointer;

  ${Styles.desktop} {
    width: 172px;
    height: 250px;
    padding-inline: 0.75rem;
  }
`;

export const ViewMoreCardWrapper = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  padding-inline: 0.75rem;
  align-items: center;
  justify-content: space-between;

  ${Styles.desktop} {
    padding-inline: 0.75rem;
  }
`;

export const ViewMoreText = styled.div`
  font-family: ${Styles.fontFamily.body};
  font-size: 1rem;
  line-height: 1.25rem;
  width: min-content;
  font-weight: 400;

  ${Styles.desktop} {
    font-size: 1.25rem;
    line-height: 1.5rem;
  }
`;

export const AddRewardCTA = styled.div`
  position: absolute;
  inset-block-start: 12px;
  inset-inline-end: 16px;
  padding: 0.5rem;
  background-color: ${Styles.color.primary};
  border-radius: 100%;
  z-index: 1;

  ${Styles.desktop} {
    display: none;
  }
`;

export const PreviousButtonInline = styled(MenuCardButton)<{ disabled: boolean }>`
  transform: rotate(180deg) translateX(25%) scale(0.85);
  ${p => p.disabled && `display: none;`}

  &:active {
    transform: rotate(180deg) translateX(25%) scale(0.95);
  }
`;

export const NextButtonInline = styled(MenuCardButton)<{ disabled: boolean }>`
  transform: ${normalizedTranslate('50%')} scale(0.85);
  ${p => p.disabled && `display: none;`}

  &:active {
    transform: ${normalizedTranslate('50%')} scale(0.95);
  }
`;
